// Admin > Requests (2026-11-05, docs/product/feedback-pipeline.md): what the feedback pipeline is working on, grouped
// from people's reports. Needs you (a product call, or a fix waiting for your OK) / In progress / Shipped / All. Each
// row: title, label, status, how many reports and from whom, the PR, why it waits, and Approve / Reject (with an
// optional note) on the ones that need you. GET /api/admin/requests?status=, POST /api/admin/requests/:id/decision.
import { useState } from "react";
import { Empty, ErrorNote, Spinner, timeAgo, useLoad } from "../components/bits";
import { Shell } from "../components/Shell";
import { canDecide, decideRequest, listRequests, REQUEST_FILTERS, STATUS_WORDS, type FixRequest, type RequestFilter } from "../pipeline";
import { Link } from "../router";
import { toast, useStore } from "../store";

export function AdminRequestsScreen() {
  const me = useStore((s) => s.me);
  const admin = me?.user.role === "admin";
  const [filter, setFilter] = useState<RequestFilter>("needs");
  const { data, error, loading, reload } = useLoad(() => (admin ? listRequests(filter) : Promise.resolve(null)), [admin, filter]);

  if (!admin)
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-primary">Back home</Link>}>
            Requests are for the studio's admins.
          </Empty>
        </div>
      </Shell>
    );

  return (
    <Shell>
      <div className="page">
        <header className="page-head">
          <h1 className="page-title">Requests</h1>
          <p className="page-lede">Fixes and changes made from people's reports. Bugs fix themselves; product calls and big fixes wait for you here.</p>
        </header>
        <div className="fb-filters" role="tablist" aria-label="Show">
          {REQUEST_FILTERS.map((f) => (
            <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} className={`chip${filter === f.id ? " is-on" : ""}`} onClick={() => setFilter(f.id)}>
              {f.label}
              {data && <span className="chip-count">{data.counts[f.id]}</span>}
            </button>
          ))}
        </div>
        {error && <ErrorNote text={`Couldn't load the requests. ${error}`} retry={reload} />}
        {loading && !data && <Spinner label="Loading" />}
        {data && !data.requests.length && <p className="muted">{filter === "needs" ? "Nothing needs you." : "Nothing here."}</p>}
        {!!data?.requests.length && (
          <ul className="fb-rows">
            {data.requests.map((r) => (
              <RequestRow key={r.id} r={r} onDone={reload} />
            ))}
          </ul>
        )}
      </div>
    </Shell>
  );
}

function RequestRow({ r, onDone }: { r: FixRequest; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (decision: "approved" | "rejected") => {
    setBusy(true);
    try {
      await decideRequest(r.id, decision, note);
      toast(decision === "approved" ? "Approved." : "Rejected.", "good");
      onDone();
    } catch (e) {
      toast(`Couldn't save that. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  const who = r.reporters.map((p) => p.name || p.email || "Someone");
  return (
    <li className="fb-row">
      <div className="fb-head">
        <span className={`rq-label rq-label-${r.label}`}>{r.label}</span>
        <span>{STATUS_WORDS[r.status] ?? r.status}</span>
        <span>
          {r.reports} report{r.reports === 1 ? "" : "s"}
        </span>
        {r.updatedAt && <span>{timeAgo(r.updatedAt)}</span>}
      </div>
      <p className="fb-text">
        <strong>{r.title}</strong>
      </p>
      {r.summary && r.summary !== r.title && <p className="fb-meta">{r.summary}</p>}
      {(who.length > 0 || r.prUrl) && (
        <p className="fb-meta">
          {who.length > 0 && <span>From {who.slice(0, 5).join(", ")}{who.length > 5 ? ` and ${who.length - 5} more` : ""}</span>}
          {who.length > 0 && r.prUrl && " · "}
          {r.prUrl && (
            <a href={r.prUrl} target="_blank" rel="noreferrer">
              PR #{r.prNumber}
            </a>
          )}
        </p>
      )}
      {r.why && <p className="fb-meta">Waiting: {r.why}</p>}
      {r.decision && (
        <p className="fb-meta">
          You {r.decision}
          {r.decisionNote ? `: ${r.decisionNote}` : "."}
        </p>
      )}
      {canDecide(r) && (
        <>
          <input className="rq-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="A note (optional, a reason if you reject)" aria-label="Note" />
          <div className="fb-acts">
            <button type="button" className="btn btn-primary btn-small" disabled={busy} onClick={() => void decide("approved")}>
              Approve
            </button>
            <button type="button" className="btn btn-quiet btn-small" disabled={busy} onClick={() => void decide("rejected")}>
              Reject
            </button>
          </div>
        </>
      )}
    </li>
  );
}
